export type ProductTier = 'BASE' | 'AVANZADO';

export type PlanFeatureKey =
  | 'core.clinic_ops'
  | 'core.email_reminders'
  | 'core.help_support'
  | 'app.patient'
  | 'comm.push'
  | 'intake.digital'
  | 'intake.custom'
  | 'comm.email_tracking_full'
  | 'comm.whatsapp'
  | 'comm.whatsapp_bot'
  | 'marketing.email'
  | 'display.caller'
  | 'rbac.advanced'
  | 'patients.global_identity'
  | 'reports.dashboard'
  | 'reports.advanced';

export interface PlanFeatureItem {
  key: PlanFeatureKey;
  label: string;
  description: string;
  group: string;
  minTier: ProductTier;
  enabled: boolean;
}

export interface PlanCommunication {
  channelEmail: boolean;
  channelPush: boolean;
  channelWhatsapp: boolean;
  emailTracking: string;
  pushTracking: string;
  whatsappTracking: string;
  patientResponseEnabled: boolean;
  manualOrchestratedRetry: boolean;
  whatsappReminderTemplateMode: WhatsAppReminderTemplateMode;
}

export type WhatsAppReminderTemplateMode = 'none' | 'plus' | 'pro';

export function parseWhatsAppReminderTemplateMode(
  raw: string | null | undefined,
): WhatsAppReminderTemplateMode {
  const value = String(raw ?? '').trim().toLowerCase();
  if (value === 'pro' || value === 'contact') return 'pro';
  if (value === 'plus') return 'plus';
  if (value === 'none') return 'none';
  if (value === 'basic') return 'plus';
  return 'none';
}

export function waReminderModeI18nKey(
  mode: string | null | undefined,
  variant: 'full' | 'short' = 'short',
): string {
  const normalized = parseWhatsAppReminderTemplateMode(mode);
  if (variant === 'full') {
    if (normalized === 'none') return 'pages.plans.waTemplateNone';
    if (normalized === 'pro') return 'pages.plans.waTemplatePro';
    return 'pages.plans.waTemplatePlus';
  }
  if (normalized === 'none') return 'pages.plans.waTemplateNoneShort';
  if (normalized === 'pro') return 'pages.plans.waTemplateProShort';
  return 'pages.plans.waTemplatePlusShort';
}

export interface PlanCatalogItem {
  id: number;
  nombre: string;
  consultorios: number;
  profesionales: number;
  precio: number;
  /** Precio para clínicas en dólares; null = sin precio USD. */
  precioUsd?: number | null;
  planActivo: boolean;
  fechaAlta?: string;
  fechaModificacion?: string;
  usuarioModificacion?: string;
  productTier: ProductTier;
  features: PlanFeatureItem[];
  communication: PlanCommunication | null;
  enabledFeatureCount: number;
}

export const TIER_LABELS: Record<ProductTier, string> = {
  BASE: 'Base',
  AVANZADO: 'Avanzado',
};

export const TIER_DESCRIPTIONS: Record<ProductTier, string> = {
  BASE: 'Operación de clínica, email y dashboard de gráficos con todas las métricas',
  AVANZADO: 'Tracking email full, RBAC e identidad multi-clínica',
};

export const TIER_INCLUDES_PREVIOUS: Record<ProductTier, string | null> = {
  BASE: null,
  AVANZADO: 'Incluye todo lo del plan Base',
};

/** Features que “introduce” cada escalón (no las heredadas). */
export function featuresIntroducedAtTier(
  features: PlanFeatureItem[],
  tier: ProductTier,
): PlanFeatureItem[] {
  return features.filter((f) => f.minTier === tier && f.enabled);
}

export function featuresInheritedBeforeTier(
  features: PlanFeatureItem[],
  tier: ProductTier,
): PlanFeatureItem[] {
  if (tier === 'BASE') return [];
  return features.filter((f) => f.minTier !== 'AVANZADO' && f.enabled);
}

export const FEATURE_GROUP_LABELS: Record<string, string> = {
  core: 'Núcleo',
  app: 'App paciente',
  communication: 'Comunicación',
  marketing: 'Marketing',
  clinical: 'Clínica',
  admin: 'Administración',
};

/**
 * Agrupación de "Ajuste fino por capacidad" en el modal de edición de plan.
 *
 * Agrupa por *qué habilita* cada capacidad (viene con cualquier plan / la
 * desbloquea el pack Coofit Conexión / la agrega el tier Avanzado) en vez de
 * por dominio, que es lo que necesita ver quien está editando un plan.
 *
 * Es solo presentación del front: el `group` que manda el API queda intacto y
 * lo sigue usando PlansPage.
 */
export type PlanFeatureUiGroup =
  | 'incluido'
  | 'conexion'
  | 'marketing'
  | 'avanzado'
  | 'otros';

export const PLAN_FEATURE_UI_GROUP_ORDER: PlanFeatureUiGroup[] = [
  'incluido',
  'conexion',
  'marketing',
  'avanzado',
  'otros',
];

export const PLAN_FEATURE_UI_GROUP_LABELS: Record<PlanFeatureUiGroup, string> = {
  incluido: 'Incluido en todos los planes',
  conexion: 'Requiere Coofit Conexión',
  marketing: 'Requiere Coofit Marketing',
  avanzado: 'Solo plan Avanzado',
  otros: 'Otras capacidades',
};

/**
 * Tipado como Record de PlanFeatureKey a propósito: si mañana se suma una key
 * al catálogo, esto deja de compilar hasta que se le asigne grupo. Las keys
 * legacy que no están en el catálogo comercial caen en 'otros' en runtime.
 */
export const PLAN_FEATURE_UI_GROUPS: Record<PlanFeatureKey, PlanFeatureUiGroup> = {
  'core.clinic_ops': 'incluido',
  'core.email_reminders': 'incluido',
  'core.help_support': 'incluido',
  'intake.digital': 'incluido',
  'intake.custom': 'avanzado',
  'display.caller': 'incluido',
  'app.patient': 'conexion',
  'comm.push': 'conexion',
  'comm.whatsapp': 'conexion',
  'comm.whatsapp_bot': 'conexion',
  'comm.email_tracking_full': 'avanzado',
  'marketing.email': 'marketing',
  'rbac.advanced': 'avanzado',
  'patients.global_identity': 'avanzado',
  'reports.dashboard': 'incluido',
  'reports.advanced': 'incluido',
};

/** Labels legibles para keys de plan_feature (incluye keys legacy fuera del catálogo comercial). */
export const FEATURE_KEY_LABELS: Record<string, string> = {
  'core.clinic_ops': 'Operación de clínica',
  'core.email_reminders': 'Recordatorios por email',
  'core.help_support': 'Ayuda y soporte',
  'app.patient': 'App paciente',
  'comm.push': 'Notificaciones push',
  'intake.digital': 'Ficha inicial (usar)',
  'intake.custom': 'Ficha inicial personalizable',
  'comm.email_tracking_full': 'Tracking completo de email',
  'comm.whatsapp': 'WhatsApp',
  'comm.whatsapp_bot': 'Agenda WhatsApp (bot, add-on de Conexión)',
  'marketing.email': 'Marketing por email',
  'display.caller': 'Llamador / display',
  'rbac.advanced': 'Roles y permisos avanzados',
  'patients.global_identity': 'Identidad global multi-clínica',
  'reports.dashboard': 'Dashboard de gráficos',
  'reports.advanced': 'Reportes avanzados',
  'clinical.dentistry': 'Odontología',
};

export function featureKeyLabel(featureKey: string): string {
  return FEATURE_KEY_LABELS[featureKey] ?? featureKey;
}

/** Plan Intermedio (Id 2): dado de baja, no forma parte del catálogo comercial. */
export function isRetiredIntermedioPlan(plan: { id: number; nombre?: string }): boolean {
  if (plan.id === 2) return true;
  return /intermedio|intermediate|intermedi[aá]rio/i.test(plan.nombre ?? '');
}
